import axios from 'axios';
import { useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

/**
 * Liefert eine axios-Instanz, die `/api/platform/...` Calls mit Auth-Cookie absetzt.
 *
 * 401-Handling (bewusst eng gefasst): Ein 401 eines beliebigen DATEN-Endpunkts
 * wirft NICHT mehr blind die ganze Session weg. Nur ein verifiziert ungueltiger
 * Session-Status (401 auf /auth/session) loggt global aus. Ein 401 eines anderen
 * Endpunkts wird (a) waehrend des Session-Restore ignoriert, (b) sonst einmal gegen
 * /auth/session re-validiert und nur bei dessen 401 ausgeloggt, (c) ansonsten an den
 * Aufrufer durchgereicht. Verhindert, dass ein einzelner 401 nach Reload eine
 * gueltige Session verwirft.
 */
const SESSION_URL = `${API_BASE}/platform/auth/session`;

export function useApi() {
  const { token, logout, sessionLoading } = useAuth();

  const instance = useMemo(() => {
    const inst = axios.create({
      baseURL: `${API_BASE}/platform`,
      timeout: 60000,
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,  // PINFRA-120: send prism_session cookie on every request
    });

    inst.interceptors.request.use((config) => {
      // Only attach Bearer if we hold a real JWT in memory (not the 'cookie' sentinel).
      // After page refresh the cookie carries auth; no Bearer needed.
      if (token && token !== 'cookie') {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    inst.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          const cfg = error.config || {};
          const method = (cfg.method || 'get').toUpperCase();
          const path = cfg.url || '';
          // Diagnose-Hilfe: den tatsaechlichen 401-Ausloeser sichtbar machen (keine Secrets).
          // eslint-disable-next-line no-console
          console.warn(`[useApi] 401 on ${method} ${path} - session NOT discarded blindly`);

          const isSessionCheck = path.includes('/auth/session');
          if (isSessionCheck) {
            // Der Session-Check selbst ist ungueltig -> echter Ablauf, global ausloggen.
            logout();
          } else if (!sessionLoading) {
            // Daten-Endpunkt-401: Session NICHT blind wegwerfen. Einmal gegen
            // /auth/session re-validieren (Rohes fetch, nicht ueber diesen Interceptor,
            // um Rekursion zu vermeiden) - nur bei dessen 401 ausloggen.
            try {
              const r = await fetch(SESSION_URL, { credentials: 'include' });
              if (r.status === 401) logout();
            } catch {
              // Netzwerkfehler bei der Re-Validierung -> Session behalten, Fehler durchreichen.
            }
          }
          // Waehrend sessionLoading (initialer Restore): nie ausloggen (transiente Races).
        }
        return Promise.reject(error);
      }
    );

    return inst;
  }, [token, logout, sessionLoading]);

  const api = useCallback(async (path, opts = {}) => {
    const method = (opts.method || 'GET').toLowerCase();
    const config = { url: path, method };
    if (opts.body !== undefined) {
      config.data = typeof opts.body === 'string' ? JSON.parse(opts.body || '{}') : opts.body;
    }
    if (opts.params) config.params = opts.params;
    if (opts.headers) config.headers = opts.headers;
    const res = await instance.request(config);
    return res.data;
  }, [instance]);

  return { api, instance };
}

/**
 * Standalone fetch fuer Login (kein Token benoetigt).
 */
export async function loginRequest(email, password) {
  const url = `${API_BASE}/platform/auth/login`;
  const res = await axios.post(url, { username: email, password }, {
    headers: { 'Content-Type': 'application/json' },
    timeout: 30000,
    withCredentials: true,  // PINFRA-120: receive Set-Cookie from login response
  });
  return res.data;
}

/**
 * Standalone (ohne Token): Passwort-Reset anfordern. Antwortet immer generisch.
 */
export async function forgotPasswordRequest(identifier) {
  const url = `${API_BASE}/platform/auth/forgot-password`;
  const res = await axios.post(url, { identifier }, {
    headers: { 'Content-Type': 'application/json' }, timeout: 30000,
  });
  return res.data;
}

/**
 * Standalone (ohne Token): neues Passwort per Einmal-Token setzen.
 */
export async function resetPasswordRequest(token, newPassword) {
  const url = `${API_BASE}/platform/auth/reset-password`;
  const res = await axios.post(url, { token, new_password: newPassword }, {
    headers: { 'Content-Type': 'application/json' }, timeout: 30000,
  });
  return res.data;
}
